import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Fab,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  ClickAwayListener,
  Portal,
} from "@mui/material";
import {
  HelpOutline as HelpOutlineIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";

const HelpButton = ({ context = "geral" }) => {
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowHelpDrawer(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setShowHelpDrawer(false);
    }, 500); // Aumentei o delay para 500ms
  }, []);

  const handleClickAway = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowHelpDrawer(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowHelpDrawer(prev => !prev);
  }, []);

  // Cleanup timeout no unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getContextMessage = () => {
    switch (context) {
      case "etiquetas":
        return "Olá! Preciso de ajuda com etiquetas e NF-e no Wala Smart Work";
      case "integracoes":
        return "Olá! Preciso de ajuda com integrações no Wala Smart Work";
      default:
        return "Olá! Tenho dúvidas sobre o Wala Smart Work";
    }
  };

  const getContextTitle = () => {
    switch (context) {
      case "etiquetas":
        return "Ajuda com Etiquetas";
      case "integracoes":
        return "Ajuda com Integrações";
      default:
        return "Como podemos ajudar?";
    }
  };

  const getContextDescription = () => {
    switch (context) {
      case "etiquetas":
        return "Dúvidas sobre impressão de etiquetas, formatação ou processamento de NF-e";
      case "integracoes":
        return "Problemas com APIs, conexões de marketplace ou configurações";
      default:
        return "Nossa equipe está pronta para esclarecer suas dúvidas sobre o sistema";
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 24, sm: 32 },
          right: { xs: 24, sm: 32 },
          zIndex: 1300,
        }}
      >
        {/* Help Drawer */}
        {showHelpDrawer && (
          <Portal>
            <Fade in={showHelpDrawer} timeout={300}>
              <Card
                sx={{
                  position: "fixed",
                  bottom: { xs: 100, sm: 110 },
                  right: { xs: 24, sm: 32 },
                  width: { xs: 320, sm: 350 },
                  maxHeight: { xs: "calc(100vh - 150px)", sm: "calc(100vh - 200px)" },
                  borderRadius: 3,
                  boxShadow: "0px 15px 40px rgba(0, 0, 0, 0.2)",
                  border: "1px solid rgba(255, 115, 35, 0.1)",
                  background: "rgba(255, 255, 255, 0.98)",
                  backdropFilter: "blur(20px)",
                  zIndex: 1400,
                  overflowY: "auto",
                  transform: showHelpDrawer ? "translateY(0) scale(1)" : "translateY(20px) scale(0.9)",
                  opacity: showHelpDrawer ? 1 : 0,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.25)",
                  },
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#FF7323",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <HelpOutlineIcon />
                    {getContextTitle()}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, lineHeight: 1.5 }}
                  >
                    {getContextDescription()}
                  </Typography>

                  <List dense sx={{ p: 0 }}>
                    <ListItem
                      component="a"
                      href={`https://wa.me/5551997971635?text=${encodeURIComponent(getContextMessage())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(37, 211, 102, 0.08)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <WhatsAppIcon sx={{ color: "#25D366", fontSize: 24 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="WhatsApp"
                        secondary="Resposta em até 2h"
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#25D366",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      />
                    </ListItem>

                    <Divider sx={{ my: 1.5, borderColor: "rgba(0,0,0,0.06)" }} />

                    <ListItem
                      component="a"
                      href="tel:+5551997971635"
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(26, 115, 232, 0.08)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PhoneIcon sx={{ color: "#1A73E8", fontSize: 24 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Telefone"
                        secondary="(51) 9 9797-1635"
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#1A73E8",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          fontFamily: "monospace",
                        }}
                      />
                    </ListItem>

                    <ListItem
                      component="a"
                      href="mailto:jonas@walaplace.com.br"
                      sx={{
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(255, 115, 35, 0.08)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <EmailIcon sx={{ color: "#FF7323", fontSize: 24 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary="jonas@walaplace.com.br"
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#FF7323",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      />
                    </ListItem>
                  </List>

                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: "rgba(37, 211, 102, 0.08)",
                      borderRadius: 2,
                      border: "1px solid rgba(37, 211, 102, 0.2)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1BAF4F",
                        fontWeight: 600,
                        textAlign: "center",
                        fontSize: "0.8rem",
                      }}
                    >
                      {context === "etiquetas" && "🏷️ Suporte especializado em etiquetas"}
                      {context === "integracoes" && "🔗 Suporte especializado em integrações"}
                      {context === "geral" && "💬 Suporte especializado em e-commerce"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Portal>
        )}

        {/* Help FAB */}
        <Fade in={true} timeout={800}>
          <Fab
            aria-label="Ajuda"
            size="medium"
            onClick={handleToggle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
              color: "#FFFFFF",
              width: 56,
              height: 56,
              boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.35)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                transform: "translateY(-4px) scale(1.1)",
                boxShadow: "0px 12px 30px rgba(255, 115, 35, 0.45)",
              },
              "&:active": {
                transform: "translateY(-2px) scale(1.02)",
                transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1)",
              },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 26 }} />
          </Fab>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
};

export default HelpButton;